import {
  Design,
  DraftBendingTextConfiguration,
  DraftConfiguration,
  DraftDesignConfiguration,
  DraftSpecialTextConfiguration,
  ProductTask,
} from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { createBendingTextDesign } from '@/utils/http/createBendingTextDesign';
import { createPimpFontDesign } from '@/utils/http/createPimpFontDesign';
import { fetchDesign } from '@/utils/http/fetchDesign';
import { requestBlob } from '@/utils/http/requestBlob';
import { applyMask } from '@/utils/masks/applyMask';
import { renderBendingText } from '@/utils/text/renderBendingText';
import { pollImageUpload, uploadDesign } from '@/utils/upload/uploadDesign';
import { getConfigurationFilterObjectUrl } from '@/features/filters/getConfigurationFilterObjectUrl';
import {
  isDraftBendingTextConfiguration,
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';

// designs uploaded by the customer should use the lookupId if available
const getUserDesignId = (designId: string) =>
  fetchDesign(designId).then(({ id, lookupId }) => lookupId || id);

const getFileExtension = (blob: Blob) => {
  try {
    return blob.type.split('/')[1];
  } catch (e) {
    return 'png';
  }
};

const uploadFilteredDesign = async (cid: number, design: Design) => {
  const filteredImage = assertDefined(getConfigurationFilterObjectUrl(cid));
  const blob = await requestBlob(filteredImage);
  const uploadedDesign = await uploadDesign(
    blob,
    `${design.name}-filtered.${getFileExtension(blob)}`
  );
  return getUserDesignId(uploadedDesign.designId!);
};

const applyImageEffect = async (configuration: DraftDesignConfiguration): Promise<ProductTask> => {
  const cid = configuration.cid;
  let design = configuration.design;
  let filterDesignId: string | undefined;

  if (configuration.filter) {
    filterDesignId = configuration.filter.designId || (await uploadFilteredDesign(cid, design));

    if (configuration.mask) {
      design = {
        ...design,
        id: filterDesignId,
      };
    } else {
      return {
        cid,
        designId: filterDesignId,
        filterDesignId,
      };
    }
  }

  const mask = configuration.mask!;
  const designUpload = await applyMask(design, mask, mask.offset, configuration.processedSize!);
  const { designId: uploadedDesignId, size } = await pollImageUpload(designUpload);
  const designId = await getUserDesignId(assertDefined(uploadedDesignId));

  return {
    cid,
    designId,
    size,
    filterDesignId,
  };
};

const applySpecialFont = async (configuration: DraftSpecialTextConfiguration) => {
  const {
    cid,
    text: {
      content,
      style: {
        font: { id: fontId },
        textAlign,
      },
    },
    task,
  } = configuration;

  const designUpload = await createPimpFontDesign(task.taskId, fontId, textAlign, content);
  const { designId: uploadedDesignId, size } = await pollImageUpload(designUpload);

  const designId = await getUserDesignId(assertDefined(uploadedDesignId));

  return {
    cid,
    designId,
    size,
  };
};

const applyBendingText = async (configuration: DraftBendingTextConfiguration) => {
  const bendingTextDesignPayload = renderBendingText(configuration);

  const { designId: uploadedDesignId, size } = await createBendingTextDesign(
    bendingTextDesignPayload
  );
  const designId = await getUserDesignId(uploadedDesignId);

  return {
    cid: configuration.cid,
    designId,
    size,
  };
};

export const createDesignsForSpecialConfigs = (
  configurations: DraftConfiguration[],
  progressCallback: () => void = () => {}
): Promise<ProductTask[]> => {
  const tasks: Promise<ProductTask>[] = [];

  configurations.forEach((configuration) => {
    if (isDraftDesignConfiguration(configuration) && (configuration.filter || configuration.mask)) {
      tasks.push(applyImageEffect(configuration));
    } else if (isDraftSpecialTextConfiguration(configuration)) {
      tasks.push(applySpecialFont(configuration));
    } else if (isDraftBendingTextConfiguration(configuration)) {
      tasks.push(applyBendingText(configuration));
    }
  });

  tasks.forEach((task) => task.then(progressCallback));

  return Promise.all(tasks);
};
